/**
 * Chat Stream Service
 * Handles SSE streaming from the LangGraph workflow API.
 * Shared by the Copilot agent (agent_type: 'copilot') and any other streaming consumer.
 */

import { ChatEvent } from '../types/chatEvents';
import { buildApiUrl, tenantApiPrefix } from '../config';

export class ChatStreamService {
  private controller: AbortController | null = null;
  
  /**
   * Send a message and stream the response
   */
  async sendMessage(
    message: string,
    threadId: string | null,
    token: string,
    tenantId: string,
    onEvent: (event: ChatEvent) => void,
    resumeData?: { type: string; response: Record<string, unknown> },
    agentType?: string,
    attachmentIds?: string[],
    surfaceContext?: Record<string, unknown>,
  ): Promise<void> {
    this.controller = new AbortController();
    
    // Use the new LangGraph endpoint with buildApiUrl to get correct backend URL
    const url = buildApiUrl(`${tenantApiPrefix(tenantId)}/chat/stream-langgraph`);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          message, 
          thread_id: threadId || undefined,
          resume_data: resumeData || undefined,
          ...(agentType ? { agent_type: agentType } : {}),
          ...(attachmentIds && attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
          ...(surfaceContext ? { surface_context: surfaceContext } : {}),
        }),
        signal: this.controller.signal,
      });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      if (!response.body) {
        throw new Error('No response body');
      }
      
      // Read the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Parse SSE events
        const { parsed, remainder } = this.parseSSE(buffer);
        buffer = remainder;
        
        // Emit each parsed event
        for (const event of parsed) {
          onEvent(event);
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Stream aborted by user');
        return;
      }
      throw error;
    }
  }
  
  /**
   * Replay a saved query and stream the response
   */
  async replayStream(
    savedQueryId: string,
    token: string,
    tenantId: string,
    onEvent: (event: ChatEvent) => void,
    newThreadId?: string,
    clientMsgId?: string
  ): Promise<void> {
    this.controller = new AbortController();
    
    const url = buildApiUrl(`${tenantApiPrefix(tenantId)}/saved-queries/${savedQueryId}/replay`);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_thread_id: newThreadId,
          client_msg_id: clientMsgId,
        }),
        signal: this.controller.signal,
      });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      if (!response.body) {
        throw new Error('No response body');
      }
      
      // Read the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Parse SSE events
        const { parsed, remainder } = this.parseSSE(buffer);
        buffer = remainder;
        
        // Emit each parsed event
        for (const event of parsed) {
          onEvent(event);
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Replay stream aborted by user');
        return;
      }
      throw error;
    }
  }
  
  /**
   * Stop the current stream
   */
  stop() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }
  
  /**
   * Parse SSE format into events
   */
  private parseSSE(buffer: string): { parsed: ChatEvent[]; remainder: string } {
    const events: ChatEvent[] = [];
    const frames = buffer.split('\n\n');
    const remainder = frames.pop() || '';
    
    for (const frame of frames) {
      if (!frame.trim()) continue;
      
      const lines = frame.split('\n');
      let eventType = 'message';
      let data = '';
      
      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          data = line.slice(5).trim();
        }
      }
      
      if (data) {
        try {
          const parsed = JSON.parse(data);
          // Ensure the event has the correct type field
          if (!parsed.type) {
            parsed.type = eventType;
          }
          console.log('[ChatStreamService] Parsed event:', parsed.type, parsed);
          events.push(parsed as ChatEvent);
        } catch (e) {
          console.error('Failed to parse SSE data:', data, e);
        }
      }
    }
    
    return { parsed: events, remainder };
  }
}
